// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')

import Promise from 'any-promise'; // use any-promisse instead of ES

/// LazyLoadView Loads "Loading" and "Timeout" shared views
/// ***************************************
const lazyLoadView = function (asyncview) {
  const asynchandler = () => ({
    component: asyncview,
    loading: require('@/shared/views/loading.vue').default, // Component to use while the component is loading
    delay: 1, // Delay before showing the loading component. default: 200 (milliseconds)
    error: require('@/shared/views/timeout.vue').default, // Fallback component in case the timeout is exceeded when loading the component
    timeout: 10000, // Time before giving up trying to load the component. default: infinity (milliseconds)
  });

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children to the view component
      return h(asynchandler, data, children);
    },
  });
};

/// Returns today date 
/// *****************************
const todayString = function () {
  return (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10);
};

/// Formats currency value to display in UI
/// options: { local: "en-ie", precision: 2, prefix: "€" }
/// **************************************************
const formatCurrencyToUi = function (value, options) {
  const ret = !value || (typeof value !== 'number' && !value.isNumeric()) ? "" : Number(value).toLocaleString(options.locale, { maximumFractionDigits: options.precision, minimumFractionDigits: options.precision});
  return ret === "" ? ret : (options.prefix ? options.prefix : "") + " " + ret;
};

/// Reads a param from configuration
/// ****************************************
const paramValue = function (store, name) {
  return filterValue(store.state.loanEnquiryConfig.Params, name);
};

/// Filters array of type [{Name: '', Value: ''}, ...]
/// Can be used with store.state.loanEnquiryConfig.Params and referenceData.SystemSettings
/// ****************************************
const filterValue = function(arr, name) {
  if (!arr) return '';
  const param = arr.filter(el => { return el.Name === name; });
  return param.length > 0 ? param[0].Value : '';
};

/// Opens a new window 
/// ****************************************
const openWindow = function(url, target, options) {
  window.open(url, target ?? "_self", options);
};

/// Returns a string from a potencial null value
/// ****************************************
const getString = function (value) {
  return value === null || value === undefined ? "" : value;
};

/// Converts object to array droping empty columns
/// ****************************************
const toArray = function (obj) {
  return Object.entries(obj).reduce((a, [k, v]) => (v ? (a[k] = v, a) : a), {});
};


/// ParseBoolValue - Parses boolean value received as string
/// ****************************************
const parseBoolValue = function (val) {
  try {
    return val === null || val == undefined ? false : (typeof (val) == 'string' ? eval(val.toLowerCase()) : val);
  }
  catch (e) { return false; }
};

/// Checks is the "required" validator function is present in the rules collection
/// ****************************************
const isRequired = function (rules) {
  if (rules !== undefined) {
    const req = rules.find(el => typeof (el) === 'function' && el.name == 'required');
    return req && req.length > 0;
  }
  return false;
};

/// Clean number function
/// ****************************************
const cleanNumber = function (value) {
  let result = "";
  if (value) {
    let flag = false;
    const arrayValue = value.toString().split("");
    for (var i = 0; i < arrayValue.length; i++) {
      if (Number.isInteger(parseInt(arrayValue[i])) || arrayValue[i] == "-") { // minus sign
        if (!flag) {
          // Remove leading zeros
          if (arrayValue[i] !== "0") {
            result = result + arrayValue[i];
            flag = true;
          }
        }
        else result = result + arrayValue[i];
      }
    }
  }
  return result;
};

export default {
  lazyLoadView,
  todayString, 
  paramValue, 
  filterValue,
  formatCurrencyToUi,
  openWindow, 
  getString,
  toArray,
  parseBoolValue,
  isRequired,
  cleanNumber
};
